import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';

/**
 * Generate an empty structure from a JSON Schema that shows the shape without
 * any actual values. This helps users understand what fields are available
 * when the config is `null` or unset.
 *
 * This function recursively traverses the schema to create a skeleton object
 * that represents the expected structure. It handles composite schemas
 * (`anyOf`, `oneOf`, `allOf`), objects, arrays, and primitive types. The
 * generated structure is displayed in the UI preview to give users a clear
 * understanding of the configuration shape even when no data is present.
 *
 * @param schema - The JSON Schema to generate structure from
 * @param path - Current path in the schema for context-aware handling
 * @returns An object/array/primitive matching the schema shape with empty
 * values
 */
export const generateEmptyStructureFromSchema = <T = unknown>(
  schema: JSONSchema7 | JSONSchema7Definition | undefined,
): T => {
  if (!schema || typeof schema === 'boolean') return {} as T;

  const schemaObj = schema as JSONSchema7;
  const {
    type,
    properties,
    items,
    anyOf,
    oneOf,
    allOf,
    enum: enumValues,
    default: defaultValue,
  } = schemaObj;

  // Handle composite schemas (anyOf, oneOf, allOf)
  if (anyOf && anyOf.length > 0) {
    const nonNullSchema = anyOf.find(
      (schemaItem): schemaItem is JSONSchema7 =>
        typeof schemaItem !== 'boolean' && schemaItem.type !== 'null',
    );
    return generateEmptyStructureFromSchema<T>(nonNullSchema || anyOf[0]);
  }
  if (oneOf && oneOf.length > 0) {
    return generateEmptyStructureFromSchema<T>(oneOf[0]);
  }
  if (allOf && allOf.length > 0) {
    let result = {} as T;
    for (const subSchema of allOf) {
      const subResult = generateEmptyStructureFromSchema(subSchema);
      if (typeof subResult === 'object' && !Array.isArray(subResult)) {
        result = { ...result, ...subResult } as T;
      }
    }
    return result;
  }

  // Handle enums by returning the first valid option or the default if specified
  if (enumValues && Array.isArray(enumValues) && enumValues.length > 0) {
    // If there's a default value specified in the schema, use it
    if (defaultValue !== undefined && enumValues.includes(defaultValue)) {
      return defaultValue as T;
    }
    // Otherwise return the first enum value as the default
    return enumValues[0] as T;
  }

  // Handle different types
  if (type === 'object' && properties) {
    const result: Record<string, unknown> = {};
    for (const [key, propSchema] of Object.entries(properties)) {
      // Special handling for nullable objects that should start as `null`.
      // Check if this property has `anyOf` with `null` and object types`.
      if (typeof propSchema !== 'boolean' && propSchema.anyOf) {
        const schemas = propSchema.anyOf.filter(
          (s): s is JSONSchema7 => typeof s !== 'boolean',
        );
        const hasNull = schemas.some(s => s.type === 'null');
        const hasObject = schemas.some(s => s.type === 'object');

        // If it's a nullable object, initialize as `null` instead of as an
        // empty object
        if (hasNull && hasObject) {
          result[key] = null;
        } else {
          result[key] = generateEmptyStructureFromSchema(propSchema);
        }
      } else {
        result[key] = generateEmptyStructureFromSchema(propSchema);
      }
    }
    return result as T;
  }

  if (type === 'array' && items) {
    return [
      generateEmptyStructureFromSchema(items as JSONSchema7Definition),
    ] as T;
  }

  // For primitive types, return empty or default values
  if (type === 'string') return '' as T;
  if (type === 'number' || type === 'integer') return 0 as T;
  if (type === 'boolean') return false as T;
  if (type === 'null') return null as T;

  // Default fallback
  return null as T;
};

/**
 * Process schema to extract non-`null` schemas from `anyOf` constructs. This
 * function mutates the schema to prepare it for RJSF form generation.
 *
 * The function recursively processes the entire schema tree, handling:
 * - Optional fields: Extracts non-`null` schemas from `anyOf` constructs
 * - Nested objects and arrays: Recursively processes child schemas
 * - Removes default values from properties within nullable objects to
 *   prevent RJSF from auto-filling them when the object is enabled
 *
 * @param obj - The schema object to process (modified in place)
 * @param isOptional - Whether the current field is optional
 * @param parentSchema - The parent schema for context
 * @param path - Current path in the schema tree (for special case detection)
 * @param isWithinNullableObject - Whether we're inside a nullable object
 */
const processEnumsInSchema = (
  obj: JSONSchema7 | JSONSchema7Definition,
  isOptional: boolean = false,
  parentSchema?: JSONSchema7,
  path: string[] = [],
  isWithinNullableObject: boolean = false,
): void => {
  if (!obj || typeof obj === 'boolean') return;

  const schemaObj = obj as JSONSchema7;

  // Check if current level is optional
  const currentIsOptional =
    isOptional ||
    (parentSchema?.anyOf &&
      parentSchema.anyOf.some(
        schemaItem =>
          typeof schemaItem !== 'boolean' && schemaItem.type === 'null',
      ));

  // Check if this schema represents a nullable object
  const isNullableObject =
    schemaObj.anyOf &&
    schemaObj.anyOf.some(
      (s): s is JSONSchema7 => typeof s !== 'boolean' && s.type === 'null',
    ) &&
    schemaObj.anyOf.some(
      (s): s is JSONSchema7 => typeof s !== 'boolean' && s.type === 'object',
    );

  // Remove default values from properties within nullable objects, but NOT
  // from enum fields. Enums need their defaults to show correct selections.
  // This prevents RJSF from auto-filling non-enum fields when the object is
  // first enabled.
  if (
    isWithinNullableObject &&
    schemaObj.default !== undefined &&
    !schemaObj.enum
  ) {
    delete schemaObj.default;
  }

  // Recursively process all properties
  if (schemaObj.properties) {
    Object.entries(schemaObj.properties).forEach(([key, propSchema]) => {
      const newPath = [...path, key];
      // Check if this property is optional
      const propIsOptional =
        !schemaObj.required?.includes(key) ||
        (typeof propSchema !== 'boolean' &&
          propSchema.anyOf &&
          propSchema.anyOf.some(
            schemaItem =>
              typeof schemaItem !== 'boolean' && schemaItem.type === 'null',
          ));

      // If property has an `anyOf` with `null`, extract the non-null schema
      if (
        typeof propSchema !== 'boolean' &&
        propSchema.anyOf &&
        Array.isArray(propSchema.anyOf)
      ) {
        const nonNullPropSchema = propSchema.anyOf.find(
          schemaItem =>
            typeof schemaItem !== 'boolean' && schemaItem.type !== 'null',
        );
        if (nonNullPropSchema && schemaObj.properties) {
          // It's optional since it had null
          schemaObj.properties[key] = nonNullPropSchema;

          // Check if this is a nullable object property
          const propIsNullableObject =
            propSchema.anyOf.some(
              (s): s is JSONSchema7 =>
                typeof s !== 'boolean' && s.type === 'null',
            ) &&
            propSchema.anyOf.some(
              (s): s is JSONSchema7 =>
                typeof s !== 'boolean' && s.type === 'object',
            );

          processEnumsInSchema(
            schemaObj.properties[key],
            true,
            schemaObj,
            newPath,
            isWithinNullableObject || propIsNullableObject,
          );
        }
      } else {
        processEnumsInSchema(
          propSchema,
          propIsOptional,
          schemaObj,
          newPath,
          isWithinNullableObject || isNullableObject,
        );
      }
    });
  }

  // Process array items
  if (schemaObj.items) {
    processEnumsInSchema(
      schemaObj.items as JSONSchema7Definition,
      currentIsOptional,
      schemaObj,
      path,
      isWithinNullableObject,
    );
  }
};

/**
 * Prepare a JSON Schema for use with React JSON Schema Form (RJSF). This
 * function transforms the server-side JSON Schema into a format optimized
 * for form generation. It performs several critical transformations:
 * 1. Extracts non-`null` schemas from `anyOf` constructs (since RJSF
 *    doesn't handle nullable fields via `anyOf` well)
 * 2. Deep clones the schema to avoid mutations affecting the original
 * 3. Removes `$schema` properties that could trigger unwanted validation
 * 4. Processes schema recursively to extract non-`null` schemas
 *
 * The result is a schema that allows users to leave optional fields empty
 * while still providing appropriate validation through our separate Zod
 * layer.
 *
 * @param schema - The original JSON Schema from the server
 * @returns A transformed schema suitable for RJSF form generation
 */
export const prepareSchema = (schema: JSONSchema7 | undefined): JSONSchema7 => {
  if (!schema) return { type: 'object' } as JSONSchema7;

  let processedSchema: JSONSchema7 = schema;

  // If schema has an `anyOf` with `null`, extract the non-null schema for RJSF
  if (schema.anyOf && Array.isArray(schema.anyOf)) {
    const nonNullSchema = schema.anyOf.find(
      schemaItem =>
        typeof schemaItem !== 'boolean' && schemaItem.type !== 'null',
    ) as JSONSchema7 | undefined;
    if (nonNullSchema) {
      processedSchema = nonNullSchema;
    }
  }

  // Deep clone to avoid mutating original
  const cleanSchema = JSON.parse(JSON.stringify(processedSchema));

  // Remove any $schema property that might trigger compilation
  delete cleanSchema.$schema;

  // Process the schema to extract non-`null` schemas from `anyOf` constructs
  processEnumsInSchema(cleanSchema, false, schema, []);

  return cleanSchema;
};
